// PublicHeader — the single canonical marketing-site nav (PRD-171 WP1/WP2).
//
// Replaces the four copy-pasted navs (.landing-nav / .upgrade-nav / .guide-nav /
// LandingPageTemplate's re-ordered variant) that had drifted apart in markup,
// CTA semantics, and theme coverage. One component means one behaviour and one
// theme story: PublicHeader.css styles itself entirely from CSS tokens, so it
// needs NO per-theme override in themeVariations.css (that absence is the fix
// for Ward #10 -- the guide nav went dark on light themes precisely because it
// had no override, and no override can be forgotten if none is needed).
//
// The trial CTA lives HERE, not in each page, so every public page sends cold
// visitors to the CIAM sign-UP form (prompt:'create'). That is what fixes P0-3
// at the root: the 20 guides used to send SEO readers to a sign-IN form.
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import BrandMark from './BrandMark';
import './PublicHeader.css';

// The canonical nav. `key` matches the activeLink prop; `/how-it-works` is
// always labelled "How It Works" (Q3a -- the guides' old methodology label is
// retired, standardized across all 29 public routes).
const NAV_LINKS = [
  { key: 'how-it-works', to: '/how-it-works', label: 'How It Works' },
  { key: 'guides',       to: '/guides',       label: 'Guides' },
  { key: 'pricing',      to: '/upgrade',      label: 'Pricing' },
];

// Optional links selected by key via the extraLinks prop (Q3b). Defined here so
// the label lives in exactly one place -- LandingPageTemplate keeps its extra
// nav item on its 10 SEO routes without re-authoring the link at the call site.
const EXTRA_LINKS = {
  solutions: { to: '/attack-surface-monitoring', label: 'Solutions' },
};

/**
 * @param {'how-it-works'|'guides'|'pricing'|null} activeLink  which nav item is current
 * @param {'default'|'minimal'} variant  'minimal' = brand + Dashboard only (Q2, ThankYou)
 * @param {string[]} extraLinks  keys into EXTRA_LINKS (e.g. ['solutions'])
 */
function PublicHeader({ activeLink = null, variant = 'default', extraLinks = [] }) {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Preserved verbatim from the per-page handlers this component replaces.
  const handleLogin = () => {
    if (isDevelopment) {
      navigate('/command-center');
    } else {
      instance.loginRedirect(loginRequest);
    }
  };
  const handleTrial = () => {
    if (isDevelopment) {
      navigate('/command-center');
    } else {
      instance.loginRedirect({ ...loginRequest, prompt: 'create' });
    }
  };

  // Minimal variant (Q2): a paying customer on /thank-you must never see a
  // marketing nav or a "Start Free Trial" button.
  if (variant === 'minimal') {
    return (
      <nav className="public-header">
        <Link to="/" className="nav-brand">
          <BrandMark size={24} className="nav-brand__mark" />
          <span className="brand-text">DriftAlarm</span>
        </Link>
        <div className="public-header__actions">
          <button type="button" className="nav-cta-primary" onClick={() => navigate('/command-center')}>
            Dashboard
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav className="public-header">
      <Link to="/" className="nav-brand">
        <BrandMark size={24} className="nav-brand__mark" />
        <span className="brand-text">DriftAlarm</span>
      </Link>
      {/* Links stay <Link>/<a>, never <button> -- validate-prerender requires
          >=1 internal a-href per route and a nav of buttons would fail it. */}
      <div className="public-header__links">
        {NAV_LINKS.map((l) => (
          <Link
            key={l.key}
            to={l.to}
            className={`nav-link-public${activeLink === l.key ? ' active' : ''}`}
          >
            {l.label}
          </Link>
        ))}
        {extraLinks
          .map((k) => EXTRA_LINKS[k])
          .filter(Boolean)
          .map((l) => (
            <Link key={l.to} to={l.to} className="nav-link-public">
              {l.label}
            </Link>
          ))}
        {/* Log-in is a TEXT link (D1): demoting it from a bordered ghost is the
            fix for "nearly invisible" -- it stops competing with the CTA and
            sheds the 1.72:1 border that made it disappear. */}
        <button type="button" className="nav-login-link" onClick={handleLogin}>
          Log in
        </button>
        <button type="button" className="nav-cta-primary" onClick={handleTrial}>
          Start Free Trial
        </button>
      </div>
    </nav>
  );
}

export default PublicHeader;
